import * as Model from "@effect/sql/Model";
import { Schema } from "effect";
import { ResellerFeeType } from "./ResellerRateTerms.ts";

/**
 * Which level of the reseller rate config resolved the applied terms.
 *
 * @since 1.0.0
 */
export const AppliedScope = Schema.Literal("reseller", "customer", "agent");
export type AppliedScope = Schema.Schema.Type<typeof AppliedScope>;

/**
 * Reseller commission lifecycle status.
 *
 * - accrued: earned on a received payment, not yet disbursed
 * - paid: rolled into a settled payout batch
 * - void: reversed by a refund, chargeback, or clawback
 *
 * @since 1.0.0
 */
export const ResellerCommissionStatus = Schema.Literal("accrued", "paid", "void");
export type ResellerCommissionStatus = Schema.Schema.Type<typeof ResellerCommissionStatus>;

/**
 * One per-fee-type line of a mixed invoice's application-fee split, snapshotted
 * onto the accrual. A pay-up-front invoice can bill BOTH a subscription line and
 * a one-time signup line, each with its own resolved terms; the single accrual
 * row records the aggregate (its `feeType` / `applicationFeeAmount` cover the
 * whole invoice) while `splitBreakdown` preserves the per-fee-type detail so
 * reporting can show subscription-split vs signup-split. `grossAmount` is the
 * line's billed amount, `applicationFeeAmount` our cut on that line, and
 * `appliedFloorAmount` / `appliedUpsideShareBps` / `appliedScope` the terms that
 * produced it. All amounts are integer cents.
 *
 * @since 1.0.0
 */
export class ResellerSplitLine extends Schema.Class<ResellerSplitLine>("ResellerSplitLine")({
  feeType: ResellerFeeType,
  grossAmount: Schema.Int,
  applicationFeeAmount: Schema.Int,
  appliedFloorAmount: Schema.Int,
  appliedUpsideShareBps: Schema.Int,
  appliedScope: AppliedScope,
}) {}

/**
 * Reseller commission model — accrual ledger, one row per qualifying
 * end-customer payment.
 *
 * Commission is earned on PAYMENT RECEIVED (Stripe `invoice.paid`), never on
 * subscription creation. `resellerCustomerId` is the reseller who earns the
 * commission; `sourceCustomerId` is the end customer who paid.
 *
 * Under the direct-charge model the reseller is merchant-of-record:
 * `grossAmount` is what the customer paid, `applicationFeeAmount` is OUR cut
 * (from `computeResellerSplit`), and `commissionAmount` is re-interpreted as the
 * reseller's share (`grossAmount − applicationFeeAmount`, kept directly).
 * `feeType` records which terms applied. `appliedFloorAmount` /
 * `appliedUpsideShareBps` / `appliedScope` snapshot the resolved terms at
 * accrual time because terms change. `commissionBps` is DEPRECATED (nullable),
 * superseded by the snapshotted floor + upside share. `periodMonth` ('YYYY-MM')
 * is the cycle the payment belongs to and `occurredAt` is when the customer
 * paid. `payoutId` is RESERVED (no payout under direct-charge). This is SHAPE
 * only — the `invoice.paid` accrual hook that writes these rows is a later
 * project.
 *
 * @since 1.0.0
 */
export class ResellerCommission extends Model.Class<ResellerCommission>("ResellerCommission")({
  id: Model.Generated(Schema.String),
  resellerCustomerId: Schema.String,
  sourceCustomerId: Schema.String,
  subscriptionId: Schema.NullOr(Schema.String),
  stripeInvoiceId: Schema.NullOr(Schema.String),
  periodMonth: Schema.String,
  grossAmount: Schema.Int,
  feeType: ResellerFeeType,
  applicationFeeAmount: Schema.Int,
  appliedFloorAmount: Schema.Int,
  appliedUpsideShareBps: Schema.Int,
  appliedScope: AppliedScope,
  commissionBps: Schema.NullOr(Schema.Int),
  commissionAmount: Schema.Int,
  // Per-fee-type split detail for a mixed (subscription + signup) invoice; null
  // for legacy/single-line accruals. Recorded so reporting can break our cut
  // down by fee type even though one accrual row covers the whole invoice.
  splitBreakdown: Schema.optionalWith(Schema.NullOr(Schema.Array(ResellerSplitLine)), { default: () => null }),
  status: Schema.optionalWith(ResellerCommissionStatus, { default: () => "accrued" as const }),
  payoutId: Schema.NullOr(Schema.String),
  occurredAt: Model.DateTimeFromDate,
  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,
}) {}

/**
 * One per-fee-type line in a {@link ResellerEarningsReport}. Non-void accruals
 * feed `grossAmount` / `applicationFeeAmount` / `accrualCount`; voided accruals
 * (refunds/chargebacks) are reported separately via `voidedGrossAmount` /
 * `voidedCount`. `resellerNetAmount` = `grossAmount − applicationFeeAmount`.
 *
 * @since 1.0.0
 */
export class ResellerEarningsLine extends Schema.Class<ResellerEarningsLine>("ResellerEarningsLine")({
  feeType: ResellerFeeType,
  grossAmount: Schema.Int,
  applicationFeeAmount: Schema.Int,
  resellerNetAmount: Schema.Int,
  accrualCount: Schema.Int,
  voidedGrossAmount: Schema.Int,
  voidedCount: Schema.Int,
}) {}

/**
 * Per-reseller earnings/reconciliation report over a `period_month` window:
 * one line per fee type present, plus totals across fee types.
 *
 * @since 1.0.0
 */
export class ResellerEarningsReport extends Schema.Class<ResellerEarningsReport>("ResellerEarningsReport")({
  resellerCustomerId: Schema.String,
  periodMonthFrom: Schema.String,
  periodMonthTo: Schema.String,
  lines: Schema.Array(ResellerEarningsLine),
  grossAmount: Schema.Int,
  applicationFeeAmount: Schema.Int,
  resellerNetAmount: Schema.Int,
}) {}
